import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Receipt, DollarSign, Search, Calendar, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Expenses() {
  const { token } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formData, setFormData] = useState({
    supplier_id: '',
    account_id: '',
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    description: '',
    amount: '',
    vat_amount: '0',
    payment_method: '',
    reference: '',
    notes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    reference: ''
  });

  useEffect(() => {
    fetchExpenses();
    fetchSuppliers();
    fetchAccounts();
  }, []);

  const fetchExpenses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setExpenses(data);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(await res.json());
    } catch (error) {
      console.error('Failed to fetch suppliers');
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAccounts(data.filter(a => a.account_type === 'expense'));
    } catch (error) {
      console.error('Failed to fetch accounts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.account_id) {
      toast.error('Please select an expense account');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/finance/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          supplier_id: formData.supplier_id || null,
          amount: parseFloat(formData.amount),
          vat_amount: parseFloat(formData.vat_amount) || 0,
          due_date: formData.due_date || null
        })
      });

      if (res.ok) {
        toast.success('Expense recorded successfully');
        setShowDialog(false);
        resetForm();
        fetchExpenses();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to record expense');
      }
    } catch (error) {
      toast.error('Failed to record expense');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    try {
      const params = new URLSearchParams({
        amount: paymentForm.amount,
        payment_method: paymentForm.payment_method
      });
      if (paymentForm.reference) {
        params.append('reference', paymentForm.reference);
      }

      const res = await fetch(`${API_URL}/api/finance/expenses/${selectedExpense.id}/pay?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Payment recorded');
        setShowPayDialog(false);
        fetchExpenses();
      } else {
        toast.error(data.detail || 'Payment failed');
      }
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      account_id: '',
      date: new Date().toISOString().split('T')[0],
      due_date: '',
      description: '',
      amount: '',
      vat_amount: '0',
      payment_method: '',
      reference: '',
      notes: ''
    });
  };

  const openPayDialog = (expense) => {
    setSelectedExpense(expense);
    setPaymentForm({
      amount: expense.balance.toString(),
      payment_method: 'bank_transfer',
      reference: ''
    });
    setShowPayDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount);
  };

  const getStatusBadge = (status) => {
    const variants = {
      paid: 'bg-green-100 text-green-800',
      approved: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${variants[status] || variants.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const stats = {
    total: expenses.reduce((sum, exp) => sum + exp.total_amount, 0),
    paid: expenses.filter(exp => exp.status === 'paid').reduce((sum, exp) => sum + exp.total_amount, 0),
    pending: expenses.reduce((sum, exp) => sum + exp.balance, 0)
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Expenses</h1>
          <p className="text-slate-500">Track and manage school expenses</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-expense-btn">
          <Plus className="w-4 h-4 mr-2" /> Record Expense
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Expenses</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.total)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Paid</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.paid)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Outstanding</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.pending)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Expense #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Account</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map(expense => (
                <TableRow key={expense.id}>
                  <TableCell className="font-mono text-sm">{expense.expense_no}</TableCell>
                  <TableCell>{expense.date}</TableCell>
                  <TableCell>{expense.supplier_name || '-'}</TableCell>
                  <TableCell className="max-w-xs truncate">{expense.description}</TableCell>
                  <TableCell>{expense.account_name}</TableCell>
                  <TableCell className="text-right">{formatCurrency(expense.total_amount)}</TableCell>
                  <TableCell className="text-right font-medium text-orange-600">
                    {formatCurrency(expense.balance)}
                  </TableCell>
                  <TableCell>{getStatusBadge(expense.status)}</TableCell>
                  <TableCell>
                    {expense.balance > 0 && (
                      <Button size="sm" variant="outline" onClick={() => openPayDialog(expense)}>
                        <DollarSign className="w-3 h-3 mr-1" /> Pay
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {expenses.length === 0 && (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No expenses recorded</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Record First Expense
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Expense Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Record Expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expense Account *</Label>
                <Select
                  value={formData.account_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, account_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select expense account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.code} - {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Supplier (Optional)</Label>
                <Select
                  value={formData.supplier_id || "none"}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, supplier_id: value === "none" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Supplier</SelectItem>
                    {suppliers.filter(s => s.is_active).map(sup => (
                      <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Amount (Before VAT) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <Label>VAT Amount</Label>
                <Input
                  type="number"
                  value={formData.vat_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, vat_amount: e.target.value }))}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What is this expense for?"
                  required
                />
              </div>
              <div>
                <Label>Reference / Invoice No.</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Supplier invoice number"
                />
              </div>
              <div>
                <Label>Payment Method (if already paid)</Label>
                <Select
                  value={formData.payment_method || "not_paid"}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value === "not_paid" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not yet paid" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_paid">Not yet paid</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="mpesa">M-Pesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>

            {formData.amount && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Amount:</span>
                  <span>{formatCurrency(parseFloat(formData.amount) || 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>VAT:</span>
                  <span>{formatCurrency(parseFloat(formData.vat_amount) || 0)}</span>
                </div>
                <div className="flex justify-between font-bold border-t pt-2 mt-2">
                  <span>Total:</span>
                  <span>{formatCurrency((parseFloat(formData.amount) || 0) + (parseFloat(formData.vat_amount) || 0))}</span>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-expense-btn">Record Expense</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pay Expense</DialogTitle>
          </DialogHeader>
          {selectedExpense && (
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Expense:</span>
                  <span className="font-mono">{selectedExpense.expense_no}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Description:</span>
                  <span>{selectedExpense.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Balance Due:</span>
                  <span className="font-bold text-orange-600">{formatCurrency(selectedExpense.balance)}</span>
                </div>
              </div>

              <div>
                <Label>Payment Amount *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  max={selectedExpense.balance}
                  step="0.01"
                  required
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select
                  value={paymentForm.payment_method}
                  onValueChange={(value) => setPaymentForm(prev => ({ ...prev, payment_method: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="mpesa">M-Pesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference</Label>
                <Input
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Cheque number or transaction reference"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowPayDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-expense-payment-btn">Record Payment</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
